import {
  IonPage,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonList,
  IonLabel,
  IonSpinner,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonCol,
  IonRow,
  IonLoading,
  useIonViewDidEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import OfflineOnlineButton from '../../../components/offlineIndicator/OfflineOnlineButton.component'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { IRootState } from '../../../store'
import { preSubmitChart } from '../../submitChart/store/submitChart.actions'
import './Share.page.css'
import LabelComponent from '../../../components/label/Label.component'
import QRCodeComponent from '../../../components/qrCode/qrCode.component'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { resetPCR } from '../../home/store/home.actions'
import { clearLocalNotifications } from '../../../utilities/localNotifications.util'
import { getRulesSelector } from '../../rules/selectors'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { runAllRules, setIsLoading } from '../../rules/store/rules.actions'

const Share: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const submitChartState = useSelector((state: IRootState) => state.submitChart)
  const { prid, emcUrl, inProgress, success, failure, responseMessage, chartLockIntended, nemsisGenerationSuccessful } =
    submitChartState
  const rulesState = useSelector(getRulesSelector)
  const { errors, isLoading } = rulesState

  useIonViewDidEnter(() => {
    dispatch(setIsLoading(true))
    dispatch(runAllRules())
  })

  const doPreSubmitChart = () => {
    dispatch(preSubmitChart())
  }

  const submitChart = () => {
    if (Object.values(errors).flat().length) {
      const alertState = {
        header: 'Warning',
        message:
          'There are existing errors that could cause the upload process to fail. Are you sure you want to continue?',
        buttons: [{ text: 'Cancel' }, { text: 'Yes', handler: doPreSubmitChart }],
      }
      dispatch(showAlert(alertState))
    } else {
      doPreSubmitChart()
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(resetPCR())
    clearLocalNotifications()
    history.push(AppRouterConstants.HOME_ROUTE)
  }

  const getSuccessMessageObject = () => {
    const messageObj = {
      message: '',
      className: '',
    }

    if (success) {
      messageObj.message = t('share.SuccessfullySubmitted')

      if (chartLockIntended && !nemsisGenerationSuccessful) {
        messageObj.message = t('share.SuccessfullyUploadedNEMSISError')
        messageObj.className = 'successfully-uploaded-nemsis-error'
      }
    }
    return messageObj
  }

  const { message: successMessage, className: successMessageClassName } = getSuccessMessageObject()

  // Old render logic removed
  // See commit b5ed4be50293192ea87158cc5e94b32750cb2b7a for previous implementation.

  return (
    <IonPage id='SharePage' data-testid='Share'>
      <PCRHeader />

      <IonHeader className='ion-no-padding'>
        <IonToolbar color='light'>
          {success && (
            <IonButtons slot='start'>
              <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary'>
                <IonIcon icon={chevronBackOutline} />
                {t('common.buttons.Back')}
              </IonButton>
            </IonButtons>
          )}
          <IonTitle>{t('share.Share')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light' className='share-content'>
        <IonList lines='full' className='share-list'>
          <IonItem>
            <img
              src='/assets/images/cloud-upload.svg'
              style={{ height: '60px', width: '60px' }}
              id='cloud-upload-icon'
            />

            <OfflineOnlineButton
              disabled={
                inProgress ||
                success ||
                Object.values(errors)
                  .flat()
                  .some((error) => error.priority === 0)
              }
              dataTestId='SubmitChartButton'
              onClick={submitChart}
              label={t('share.buttons.SubmitChart')}
            />
          </IonItem>

          {inProgress && (
            <IonItem data-testid='SubmitChartInProgress'>
              <IonSpinner color='primary'></IonSpinner>
              <IonLabel className='ion-padding'>{t('share.SubmittingChart')}</IonLabel>
            </IonItem>
          )}

          {success && (
            <>
              <IonItem data-testid='SubmitChartSuccess'>
                <IonCol>
                  <IonRow class='ion-padding'>
                    <IonLabel class={`ion-text-wrap ${successMessageClassName}`}>{successMessage}</IonLabel>
                  </IonRow>

                  <IonRow class='ion-padding'>
                    <IonLabel class='ion-text-wrap'>{t('share.NewPRID')}</IonLabel>
                    <IonLabel>&nbsp;</IonLabel>
                    <LabelComponent isPHI className='ion-text-wrap' color='success' label={prid} />
                  </IonRow>

                  {emcUrl && (
                    <>
                      <IonRow class='ion-padding'>
                        <IonLabel class='ion-text-wrap'>{t('share.ClickLinkOrQrCodeBelow')}</IonLabel>
                      </IonRow>

                      <IonRow>
                        <IonItem lines='none' href={emcUrl} target='_blank'>
                          <LabelComponent isPHI className='ion-text-wrap' color='primary' label={emcUrl} />
                        </IonItem>
                      </IonRow>

                      <IonRow class='ion-justify-content-center ion-padding'>
                        <QRCodeComponent
                          isPHI
                          data-testid='QRCodeEmcURL'
                          value={emcUrl}
                          size={180}
                          level='H'
                          includeMargin={false}
                        />
                      </IonRow>
                    </>
                  )}
                </IonCol>
              </IonItem>
            </>
          )}

          {Object.values(errors).flat().length > 0 && (
            <IonItem data-testid='SubmitChartFailure'>
              <IonCol>
                <IonRow class='ion-padding'>
                  <IonLabel class='ion-text-wrap'>{t('share.PleaseAddressIssues')}</IonLabel>
                </IonRow>
              </IonCol>
            </IonItem>
          )}

          {failure && responseMessage && (
            <IonItem data-testid='SubmitChartFailure'>
              <IonCol>
                <IonRow class='ion-padding'>
                  <IonLabel class='ion-text-wrap'>{t('share.Failure')}</IonLabel>
                </IonRow>
              </IonCol>
            </IonItem>
          )}
        </IonList>

        {/* {!location?.state?.hidePreUploadComponents && !isLoading && !success && (
          <IonList lines='none' data-testid='XSDErrorList' className='share-list'>
            {Object.keys(groupedRulesErrors).map((parentLabel) => (
              <>{renderErrorGroup(parentLabel)}</>
            ))}
          </IonList>
        )} */}
      </IonContent>
      {isLoading && (
        <IonLoading
          data-testid='Loader'
          isOpen
          animated
          showBackdrop
          spinner={'crescent'}
          message={t('rules.loading')}
        />
      )}
    </IonPage>
  )
}

export default Share
